import axios from 'axios';
import { BASE_URL, CONTENT_ASSET_API_PATH } from 'constants/api';
import { ApiService } from 'service/api/apiService';
import { ContentVideoAssetHistory, VideoAssetSet } from '../types/contentVideoType';

class ContentAssetService extends ApiService<VideoAssetSet> {
  public async fetchContentVideoAssets(contentId: number): Promise<VideoAssetSet[]> {
    const response: VideoAssetSet[] = (await this.fetchById(contentId)) as any;
    return response; // Return all content video assets (removed is_default filter)
  }

  // Save new asset set (existing)
  public async saveContentVideoAssets(
    contentId: number,
    videoAssetSets: VideoAssetSet[]
  ): Promise<void> {
    await Promise.all(
      videoAssetSets.map(async (videoAssetSet: VideoAssetSet) => {
        await this.create({
          content: contentId,
          video: videoAssetSet.video!,
          subtitle_ids: videoAssetSet.subtitle_ids!,
          is_default: videoAssetSet.is_default!,
          primary: videoAssetSet.primary ?? false,
          service_name: videoAssetSet.service_name,
          stream_manifests: videoAssetSet.stream_manifests,
          is_subtitles_embedded: videoAssetSet.is_subtitles_embedded,
          after_add_subtitle: videoAssetSet.after_add_subtitle || false,
        });
      })
    );
  }

  // Update existing asset set (newly added)
  public async updateContentVideoAsset(
    assetId: number,
    updateData: Partial<VideoAssetSet>
  ): Promise<VideoAssetSet> {
    return await this.update(assetId, updateData);
  }

  // Delete asset set (use if needed)
  public async deleteContentVideoAsset(assetId: number): Promise<void> {
    await this.delete(assetId);
  }

  /**
   * Fetch individual Content Video Asset (including stream_manifests)
   */
  public async fetchContentVideoAssetById(assetId: number): Promise<VideoAssetSet> {
    const response = await axios.get(`${BASE_URL}${CONTENT_ASSET_API_PATH}${assetId}/`);
    return response.data;
  }

  public async fetchContentVideoAssetHistory(
    contentId: number
  ): Promise<ContentVideoAssetHistory[]> {
    const response: ContentVideoAssetHistory[] = await this.fetchChildItemsByParentId(
      contentId,
      'histories/',
      new URLSearchParams({ content__id: contentId.toString() })
    );
    return response;
  }

  // Add subtitle to content video asset (regenerate stream manifests)
  public async addSubtitle(assetId: number): Promise<void> {
    await this.createBySubPath(`${assetId}/add-subtitle/`, {});
  }
}

export const contentAssetService = new ContentAssetService(CONTENT_ASSET_API_PATH);
